// Derives a human display label for a campaign + optional ETA.
// Window awareness uses operating_hours from /api/settings when window.mode === "global".
import { DateTime } from "luxon";

export function isWithinWindow(campaign, settings) {
    const sw = campaign?.send_window || { mode: "global" };
    if (sw.mode === "24h") return true;
    const cfg = sw.mode === "custom" ? sw : (settings?.operating_hours || {});
    const days = cfg.days?.length ? cfg.days : [0, 1, 2, 3, 4];
    const start = cfg.start || "09:00";
    const end = cfg.end || "17:00";
    const tz = cfg.timezone || settings?.operating_hours?.timezone || "America/Los_Angeles";

    let now;
    try { now = DateTime.now().setZone(tz); } catch { now = DateTime.now(); }
    // luxon weekday is 1..7 (Mon..Sun); we use 0..6 (Mon..Sun)
    const weekday = (now.weekday + 6) % 7;
    if (!days.includes(weekday)) return false;
    const [sh, sm] = start.split(":").map(n => parseInt(n, 10));
    const [eh, em] = end.split(":").map(n => parseInt(n, 10));
    const nowMin = now.hour * 60 + now.minute;
    return nowMin >= (sh * 60 + sm) && nowMin < (eh * 60 + em);
}

function nextWindowOpen(campaign, settings) {
    const sw = campaign?.send_window || { mode: "global" };
    const cfg = sw.mode === "custom" ? sw : (settings?.operating_hours || {});
    const days = cfg.days?.length ? cfg.days : [0, 1, 2, 3, 4];
    const start = cfg.start || "09:00";
    const tz = cfg.timezone || settings?.operating_hours?.timezone || "America/Los_Angeles";
    let now;
    try { now = DateTime.now().setZone(tz); } catch { now = DateTime.now(); }
    for (let i = 0; i < 8; i++) {
        const cand = now.plus({ days: i });
        const wk = (cand.weekday + 6) % 7;
        if (!days.includes(wk)) continue;
        const [sh, sm] = start.split(":").map(n => parseInt(n, 10));
        const startOfDay = cand.set({ hour: sh, minute: sm, second: 0, millisecond: 0 });
        if (startOfDay > now) return startOfDay;
    }
    return null;
}

export function getCampaignDisplay(campaign, settings) {
    if (!campaign) return { label: "—", tone: "neutral" };
    const status = campaign.status;
    if (status === "completed") return { label: "Completed", tone: "primary" };
    if (status === "draft") return { label: "Draft", tone: "neutral" };
    if (status === "archived") return { label: "Archived", tone: "neutral" };
    if (status === "scheduled") {
        if (campaign.scheduled_at) {
            const dt = DateTime.fromISO(campaign.scheduled_at);
            return { label: `Scheduled · ${dt.toRelative()}`, tone: "info" };
        }
        return { label: "Scheduled", tone: "info" };
    }
    if (status === "paused") {
        const next = nextWindowOpen(campaign, settings);
        if (next) return { label: `Paused · resumes ${next.toRelative()}`, tone: "warning" };
        return { label: "Paused", tone: "warning" };
    }
    if (status === "running") {
        if (isWithinWindow(campaign, settings)) return { label: "Running now", tone: "success" };
        const next = nextWindowOpen(campaign, settings);
        if (next) return { label: `Outside hours · resumes ${next.toRelative()}`, tone: "warning" };
        return { label: "Outside operating hours", tone: "warning" };
    }
    return { label: status, tone: "neutral" };
}

export function getCampaignETA(campaign) {
    if (!campaign || campaign.status === "completed") return null;
    const remaining = Math.max(0, (campaign.target_count || 0) - (campaign.sent_count || 0));
    const rate = campaign.send_rate_per_hour || 0;
    if (!remaining || !rate) return null;
    const hours = remaining / rate;
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0 && m === 0) return "<1m";
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
}
